/**
* AnalyticsHelper.ts
* @author Abhilash Panwar (abpanwar) Hector Hernandez (hectorh)
* @copyright Microsoft 2020
* File containing helper methods for EI Analytics.
* Copied from 1DS SDK.
*/
import { getLocation, getDocument } from '@microsoft/1ds-core-js';
import { _emptyString } from '../properties/Constants';
var which = 'which';
var button = 'button';
var keyCode = 'keyCode';
var max_content_name = 200;
var A = 'A';
var AREA = 'AREA';
var IMG = 'IMG';
var INPUT = 'INPUT';
export var _clickCaptureInputTypes = { BUTTON: true, CHECKBOX: true, RADIO: true, RESET: true, SUBMIT: true };
/**
* Determines whether an event is a right click or not
* @param evt - Mouse event
* @returns true if the event is a right click
*/
export function _isRightClick(evt) {
    if (which in evt) { // Chrome, FF, ...
        return (evt.which === 3);
    }
    else if (button in evt) { // IE, ...
        return (evt.button === 2);
    }
}
/**
* Determines whether an event is a left click or not
* @param evt - Mouse event
* @returns true if the event is a left click
*/
export function _isLeftClick(evt) {
    if (which in evt) { // Chrome, FF, ...
        return (evt.which === 1);
    }
    else if (button in evt) { // IE, ...
        return (evt.button === 1);
    }
}
/**
* Determines whether an event is a middle click or not
* @param evt - Mouse event
* @returns true if the event is a middle click
*/
export function _isMiddleClick(evt) {
    if (which in evt) { // Chrome, FF, ...
        return (evt.which === 2);
    }
    else if (button in evt) { // IE, ...
        return (evt.button === 4);
    }
}
/**
*  Determines whether an event is a keyboard enter or not
* @param evt - Keyboard event
* @returns true if the event is a keyboard enter
*/
export function _isKeyboardEnter(evt) {
    if (keyCode in evt) { // Chrome, FF, ...
        return (evt.keyCode === 13);
    }
}
/**
*  Determines whether an event is a keyboard space or not
* @param evt - Keyboard event
* @returns true if the event is a space enter
*/
export function _isKeyboardSpace(evt) {
    if (keyCode in evt) { // Chrome, FF, ...
        return (evt.keyCode === 32);
    }
}
/**
 * Gets the content tags in the element.
 * @param element The element to parse for content.
 */
export function _getContent(element) {
    return {
        c: element.className,
        id: element.id,
        n: _getDefaultContentName(element)
    };
}
/**
 *
* Get click target
* @returns Click target URI
*/
export function _getClickTarget(element) {
    var clickTarget = _emptyString;
    switch (element.tagName) {
        case A:
        case AREA:
            clickTarget = element.href;
            break;
        case IMG:
            clickTarget = _getImageHref(element);
            break;
        case INPUT:
            var type = element.type;
            if (type && (_clickCaptureInputTypes[type.toUpperCase()])) {
                var loc = getLocation() || {};
                if (element.form) {
                    clickTarget = element.form.action || loc.pathname;
                }
                else {
                    clickTarget = loc.pathname;
                }
            }
            break;
        default:
            break;
    }
    return clickTarget;
}
export function _getPathNameFromUri(uri) {
    var location = document.createElement('a');
    location.href = uri;
    return location.pathname;
}
/**
* Gets the default content name.
* @param element - An html element
* @returns Content name
*/
/*ignore jslint start*/
function _getDefaultContentName(element) {
    if (!element.tagName) {
        return _emptyString;
    }
    var doc = getDocument() || {};
    var contentName;
    switch (element.tagName) {
        case A:
            contentName = doc.all ? element.innerText || element.innerHTML : element.text || element.innerHTML;
            break;
        case IMG:
        case AREA:
            contentName = element.alt;
            break;
        default:
            contentName = element.value || element.name || element.alt || element.innerText || element.id;
    }
    return contentName.substring(0, max_content_name);
}
/**
* Get Image href of a given HTMLImageElement
* @param element - An html image element
* @returns Href value.
*/
function _getImageHref(element) {
    if (element) {
        var parent_1 = _findClosestAnchor(element);
        if (parent_1.length === 1) {
            if (parent_1[0].href) {
                return parent_1[0].href;
            }
            else if (parent_1[0].src) {
                return (parent_1[0].src);
            }
        }
    }
    return _emptyString;
}
/**
* Walks up DOM tree to find anchor element
* @param element - DOM element
* @returns Dom element which is an anchor
*/
function _findClosestAnchor(element) {
    /// <summary> Walks up DOM tree to find anchor element </summary>
    /// <param type='object'> DOM element </param>
    /// <returns> Dom element which is an anchor</returns>
    return _walkUpDomChainWithElementValidation(element, _isElementAnAnchor);
}
/**
* Walks up DOM tree to find element which matches validationMethod
* @param el - DOM element
* @param validationMethod - DOM element validation method
* @param validationMethodParam - DOM element validation method parameters
* @returns Dom element which is an anchor
*/
function _walkUpDomChainWithElementValidation(el, validationMethod, validationMethodParam) {
    var element = el;
    if (element) {
        //TODO(abpanwar): Handle element if jQuery
        //element = _returnDomObjectIfjQuery(element);
        while (!validationMethod(element, validationMethodParam)) {
            element = element.parentNode;
            //element = _returnDomObjectIfjQuery(element);
            if (!element || !(element.getAttribute)) {
                return null;
            }
        }
        return element;
    }
}
/**
* Determine if DOM element is an anchor
* @param element - DOM element
* @returns Is element an anchor
*/
function _isElementAnAnchor(element) {
    return element.nodeName === A;
}
/**
* Generate hashcode from a string
* @param value - The string to generate hashcode
* @returns The generated hashcode
*/
export function _hashCode(value) {
    if (value) {
        var hash = 0;
        var len = value.length;
        var i = 0;
        if (len > 0) {
            while (i < len) {
                hash = (hash << 5) - hash + value.charCodeAt(i++) | 0;
            }
        }
        return hash;
    }
}
;
//# sourceMappingURL=AnalyticsHelper.js.map